import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { Input, Modal } from '@arco-design/web-react';
import { IconCheck, IconClockCircle, IconDown, IconLocation, IconSearch, IconUser } from '@arco-design/web-react/icon';

import { ConfigContext } from '@/components/ConfigProvider/configProvider';
import { IconCluster } from '@/components/Icon/IconCluster';
import { Indicator } from '@/components/Indicator/Indicator';
import TenantBar from '@/components/TenantBar';
import useClusters from '@/hooks/useClusters';
import { getModule } from '@/hooks/useRouteScope';
import useTenants from '@/hooks/useTenants';
import { getDcAndValByTenant, getDcListAndVal } from '@/layout/servies';
import { fetchData } from '@/pages/clusters/pages/BaseInfo/service';
import { formatIsoDate } from '@/utils/date-tools';
import { isValidClusterName } from '@/utils/tenantClusterStorage';

import styles from '../styles/index.module.less';

const DATA_CENTER_POLL_INTERVAL = 10 * 1000;
/** 列表 / Banner 内嵌场景默认自适应宽度，避免在 flex 标题行被撑满 */
const DATA_CENTER_SELECT_STYLE = {
  backgroundColor: 'rgb(var(--gray-1))',
  width: 'auto',
  flex: '0 0 auto',
} as const;
const CLUSTER_BAR_BOX_STYLE = {
  backgroundColor: 'rgb(var(--gray-1))',
  width: '20vw',
  height: 36,
  lineHeight: '36px',
  padding: '0 15px',
  textAlign: 'left' as const,
  cursor: 'text',
};
const MODULE_WRAPPER_STYLE = { display: 'flex', gap: 10 };

// 顶部导航中数据中心选择器使用的精简字段。
interface ClusterItem {
  name: string;
  live?: boolean;
  /** 数据中心别名，使用后端 displayName 字段展示和搜索。 */
  displayName?: string;
  provinceName?: string;
  countyName?: string;
  cityName?: string;
  creatorName?: string;
  ownerName?: string;
  createTime?: string;
}

function replaceClusterInPath(pathname: string, from: string, to: string) {
  // 只替换 /cluster/:name 段，避免误伤租户名等其它路径片段。
  return pathname.replace(`/cluster/${from}`, `/cluster/${to}`);
}

function syncClusterParamToUrl(
  currentCluster: string | undefined,
  list: ClusterItem[],
  setClusterFn: (name: string) => void,
  navigate: (path: string, opts: { replace: boolean }) => void,
) {
  // 路由尚未解析出 cluster 时不要 replace。
  if (!currentCluster) return;

  const matched = list?.find((i) => i.name === currentCluster);
  if (matched) return;

  // 当前 cluster 非法（含字面量 "undefined"）或不在列表中时，回退到可用项或占位符。
  if (list?.length > 0) {
    const item = list[0];
    if (!item?.name) return;
    if (isValidClusterName(item.name)) {
      setClusterFn(item.name);
    }
    const nextPath = replaceClusterInPath(window.location.pathname, currentCluster, item.name);
    if (nextPath !== window.location.pathname) {
      navigate(nextPath, { replace: true });
    }
    return;
  }

  if (currentCluster !== ':cluster') {
    const nextPath = replaceClusterInPath(window.location.pathname, currentCluster, ':cluster');
    if (nextPath !== window.location.pathname) {
      navigate(nextPath, { replace: true });
    }
  }
}

let timer: ReturnType<typeof setInterval> | null = null;

interface ChangeDataCenterProps {
  style?: React.CSSProperties;
  showStatus?: boolean;
  showPrefix?: boolean;
  /**
   * 历史兼容：切换后始终停留当前页面并刷新。
   * 数据中心 / 租户模式均不再跳转 overview。
   */
  refreshCurrentPage?: boolean;
}

/** 数据中心与租户控制台共用的选择弹窗，展示别名、按编码切换。 */
export const ChangeDataCenter = ({ style = {}, showStatus = true, showPrefix = true }: ChangeDataCenterProps) => {
  const { t } = useTranslation();
  const { clusterList, tenantClusters, setClusterList, isLogin, cluster, tenantClustersLoading, loading, setTenantClusters } =
    useContext(ConfigContext);
  const { setCluster } = useClusters();
  const { setCluster: setTenantCluster, tenant } = useTenants();
  const params = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const module = getModule();
  const list = module === 'console' ? tenantClusters : clusterList;
  const value = params.cluster || cluster || undefined;
  const [modalVisible, setModalVisible] = useState(false);
  const [search, setSearch] = useState('');

  const currentItem = list?.find((i) => i.name === value);
  const displayValue = currentItem?.displayName || currentItem?.name || value;

  // 搜索仅在本地列表中筛选，列表数据由打开弹窗和轮询逻辑刷新。
  const filteredList = search
    ? list?.filter(
        (i) => (i.displayName || '').toLowerCase().includes(search.toLowerCase()) || i.name.toLowerCase().includes(search.toLowerCase()),
      )
    : list;

  const getClusterList = useCallback(
    async (forceUpdate = false) => {
      const { data } = await getDcListAndVal({ isLogin });
      const clusterMap = JSON.parse(sessionStorage.getItem('clusterMap') || '{}');
      let flag = false;
      // 轮询时只在在线状态变化后更新全局列表，减少无意义的导航栏重渲染。
      for (let i = 0; i < data?.length; i++) {
        if (clusterMap[data[i].name] !== data[i].live) {
          flag = true;
          break;
        }
      }
      (forceUpdate || flag) && setClusterList(data);
    },
    [isLogin, setClusterList],
  );

  const getTenantClusterList = useCallback(async () => {
    if (!tenant) return;
    // 控制台模式下切换的是当前租户可用的数据中心，打开弹窗时绕过缓存刷新。
    const { data } = await getDcAndValByTenant({ tenant, useCache: false });
    setTenantClusters(data);
  }, [tenant, setTenantClusters]);

  const handleModalOpen = () => {
    setModalVisible(true);
    setSearch('');
    // 每次打开弹窗都重新拉取一次列表，避免使用顶部导航中的旧缓存。
    if (module === 'console') {
      getTenantClusterList();
      return;
    }
    getClusterList(true);
  };

  useEffect(() => {
    if (loading) return;
    // 全局列表加载完成后校正 URL 中的数据中心，避免地址栏保留无效 cluster。
    if (module === 'console') {
      if (tenantClustersLoading) return;
      syncClusterParamToUrl(params?.cluster, tenantClusters, setTenantCluster, navigate);
    } else {
      syncClusterParamToUrl(params?.cluster, clusterList, setCluster, navigate);
    }
  }, [tenantClustersLoading, params.cluster, tenantClusters, module, clusterList, loading, setCluster, setTenantCluster, navigate]);

  useEffect(() => {
    // 数据中心模式下保持在线状态轮询，用于状态点和弹窗禁用态。
    if (pathname?.startsWith('/data-center')) {
      if (timer) clearInterval(timer);
      timer = setInterval(() => {
        getClusterList();
      }, DATA_CENTER_POLL_INTERVAL);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [pathname, getClusterList]);

  useEffect(() => {
    // 记录上一次在线状态快照，供轮询时判断是否需要更新列表。
    const obj: Record<string, boolean> = {};
    clusterList?.forEach((i) => (obj[i.name] = i.live));
    sessionStorage.setItem('clusterMap', JSON.stringify(obj));
  }, [clusterList]);

  const handleChange = (nextValue: string) => {
    if (nextValue !== params?.cluster && params?.cluster) {
      // 数据中心切换会影响 API 前缀，直接刷新页面确保全局上下文完全更新；保持当前路由不变。
      const oldCluster = params.cluster;
      let nextPath: string;
      if (module === 'console' || pathname.includes(`/cluster/${oldCluster}`)) {
        nextPath = pathname.replace(`/cluster/${oldCluster}`, `/cluster/${nextValue}`);
      } else {
        nextPath = pathname.replace(`/data-center/${oldCluster}`, `/data-center/${nextValue}`);
      }
      window.location.replace(nextPath);
    }
  };

  const handleItemClick = (name: string) => {
    handleChange(name);
    setModalVisible(false);
    setSearch('');
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setSearch('');
  };

  return (
    <>
      <div className={styles['select-trigger']} style={{ ...DATA_CENTER_SELECT_STYLE, ...style }} onClick={handleModalOpen}>
        {showPrefix && (
          <span className={styles['select-trigger-prefix']}>
            <IconCluster style={{ fontSize: 14 }} />
            {t('common:data.center.with.colon')}
          </span>
        )}
        {list?.length > 0 ? (
          <span className={styles['select-trigger-value']} title={displayValue}>
            {showStatus && currentItem && <Indicator status={currentItem.live ? 'success' : 'default'} style={{ marginRight: 8 }} />}
            {displayValue || t('common:please.select.a.data.center')}
          </span>
        ) : (
          <span className={styles['select-trigger-placeholder']}>
            {t('common:no.available.data.centers.please.contact.administrator.to.add')}
          </span>
        )}
        <IconDown className={styles['select-trigger-arrow']} />
      </div>

      {/* 弹窗选择数据中心 */}
      <Modal
        title={t('components:select.a.data.center')}
        visible={modalVisible}
        footer={null}
        onCancel={handleModalClose}
        style={{ width: '40vw' }}
        maskClosable={true}
      >
        <Input
          className={styles['modal-search']}
          prefix={<IconSearch />}
          placeholder={t('common:search')}
          value={search}
          onChange={setSearch}
          allowClear
        />
        {/* 数据中心列表 */}
        <div className={styles['modal-list']}>
          {filteredList?.length === 0 ? (
            <div className={styles['modal-empty']}>
              {list?.length === 0
                ? t('common:no.available.data.centers.please.contact.administrator.to.add')
                : t('components:no.matching.result')}
            </div>
          ) : (
            filteredList?.map((item) => {
              const isActive = item.name === value;
              const isDisabled = !item.live;
              const itemClass = [
                styles['modal-item'],
                isActive ? styles['modal-item-active'] : '',
                isDisabled ? styles['modal-item-disabled'] : '',
              ]
                .filter(Boolean)
                .join(' ');

              const locationStr = [item.provinceName, item.countyName, item.cityName].filter(Boolean).join('/');

              return (
                <div key={item.name} className={itemClass} onClick={() => !isDisabled && handleItemClick(item.name)}>
                  <div className={styles['modal-item-icon']}>
                    <IconCluster style={{ fontSize: 20 }} />
                  </div>
                  <div className={styles['modal-item-info']}>
                    <div className={styles['modal-item-name-row']}>
                      <span className={styles['modal-item-name']} title={item.displayName}>
                        {item.displayName || '-'}
                      </span>
                      {item.name && <div className={styles['modal-item-desc']}>({item.name})</div>}
                      {showStatus && <Indicator status={item.live ? 'success' : 'default'} />}
                      <span
                        className={styles['modal-item-status']}
                        style={{
                          color: item.live ? 'rgb(var(--green-6))' : 'rgb(var(--gray-6))',
                          fontSize: '12px',
                        }}
                      >
                        {item.live ? t('common:online') : t('common:offline')}
                      </span>
                    </div>
                    <div className={styles['modal-item-meta']}>
                      {locationStr && (
                        <span className={styles['modal-item-meta-tag']}>
                          <IconLocation />
                          {t('common:region.with.colon')}
                          {locationStr}
                        </span>
                      )}
                      {item.ownerName && (
                        <span className={styles['modal-item-meta-tag']}>
                          <IconUser />
                          {t('common:owner.with.colon')}
                          {item.ownerName}
                        </span>
                      )}
                      {item.creatorName && (
                        <span className={styles['modal-item-meta-tag']}>
                          <IconUser />
                          {t('common:creator.with.colon')}
                          {item.creatorName}
                        </span>
                      )}
                      {item.createTime && (
                        <span className={styles['modal-item-meta-tag']}>
                          <IconClockCircle />
                          {t('common:created.time.with.colon')}
                          {formatIsoDate(item.createTime, 'YYYY-MM-DD HH:mm')}
                        </span>
                      )}
                    </div>
                  </div>
                  {isActive && <IconCheck className={styles['modal-item-check']} />}
                </div>
              );
            })
          )}
        </div>
      </Modal>
    </>
  );
};

export const ClusterBar = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<any>({});
  const params = useParams();
  const clusterName = sessionStorage.getItem('clusterName') || params?.cluster;
  const value = clusterName || params?.subcluster || params?.cluster;

  useEffect(() => {
    // 集群模式只展示当前集群基础信息，不参与数据中心切换逻辑。
    const getInfo = async () => {
      try {
        const res = (await fetchData()) as any;
        setData(res);
      } catch (e) {
        console.log(e);
      }
    };
    getInfo();
  }, []);

  return (
    <div style={CLUSTER_BAR_BOX_STYLE}>
      <span style={{ marginRight: 12, color: 'var(--color-text-2)', fontSize: '12px' }}>{t('common:cluster.with.colon')}</span>
      {value || t('components:cluster.unavailable')}
      <span style={{ color: 'rgba(0, 0, 0, 0.4)', fontSize: '12px', marginLeft: 10 }}>{data.description}</span>
    </div>
  );
};

const ModuleItem = ({ children, disableHover = false }: { children: React.ReactNode; disableHover?: boolean }) => (
  <span className={!disableHover ? styles['navbar-right-btn'] : ''} style={MODULE_WRAPPER_STYLE}>
    {children}
  </span>
);

export default function ChangeModule() {
  const { mode, isLogin } = useContext(ConfigContext);
  const module = getModule();
  const isCluster = module === 'cluster' || module === 'wsp';

  if (!isLogin) return null;

  // 商品页只需要租户选择，数据中心入口由模式切换后展示。
  if (module === 'product') {
    return (
      <ModuleItem>
        <TenantBar />
      </ModuleItem>
    );
  }

  return (
    <>
      {/* 控制台模式（含各子中心）可切换租户；数据中心切换器由 TagFilters / PageBanner / 总览标题区承载。 */}
      {mode === 'console' && !isCluster && (
        <ModuleItem>
          <TenantBar />
        </ModuleItem>
      )}
      {/* 集群模块只显示当前集群，不允许在这里切换数据中心。 */}
      {isCluster && (
        <ModuleItem disableHover>
          <ClusterBar />
        </ModuleItem>
      )}
    </>
  );
}
